import { Flex, Box } from '@chakra-ui/react';
import NavItem from './NavItem';
import {
    FiInfo,
    FiClock,
    FiStar,
    FiDollarSign,
    FiGlobe,
    FiPlay,
    FiMail
} from 'react-icons/fi';

const BottomNav = ({ activeSection, onSectionClick }) => {
    const tabs = [
        { id: 'about', icon: FiInfo, label: 'About' },
        { id: 'whypiepos', icon: FiStar, label: 'Why PiePOS' },
        { id: 'pricing', icon: FiDollarSign, label: 'Pricing' },
        { id: 'demo', icon: FiPlay, label: 'Demo' },
        { id: 'ownadomain', icon: FiGlobe, label: 'Own A Domain' },
        { id: 'history', icon: FiClock, label: 'History' },
        { id: 'contact', icon: FiMail, label: 'Contact' }
    ];

    const handleClick = (tab) => {
        onSectionClick(tab.id);
    };

    const activeIndex = tabs.findIndex(tab => tab.id === activeSection);

    return (
        <Flex
            justify="space-between"
            align="center"
            width="95%"
            maxW="850px"
            mx="auto"
            mb={6}
            bg="rgba(255, 255, 255, 1)"
            backdropFilter="blur(40px) saturate(200%)"
            WebkitBackdropFilter="blur(40px) saturate(200%)"
            border="1px solid rgba(255, 255, 255, 0.18)"
            borderRadius="24px"
            p="6px"
            position="relative"
            overflow="hidden"
            boxShadow="0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.15)"
            transform="translateY(0px)"
            transition="all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
            _hover={{
                transform: 'translateY(-2px) scale(1.02)',
                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                bg: "rgba(255, 255, 255, 0.15)",
                border: "1px solid rgba(255, 255, 255, 0.25)"
            }}
            _before={{
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 50%, rgba(255, 255, 255, 0.1) 100%)',
                borderRadius: '24px',
                pointerEvents: 'none',
                zIndex: 1
            }}
        >
            {/* Dynamic active indicator with morphing animation */}
            <Box
                position="absolute"
                top="6px"
                bottom="6px"
                // Base calculation
                left={`calc(${(activeIndex * 100) / tabs.length}% + 6px)`}
                width={`calc(${100 / tabs.length}% - 12px)`}
                bg="rgba(255, 255, 255, 0.3)"
                backdropFilter="blur(20px)"
                WebkitBackdropFilter="blur(20px)"
                border="1px solid rgba(255, 255, 255, 0.4)"
                borderRadius="20px"
                transition="all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)"
                boxShadow="0 4px 20px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.5)"
                zIndex={2}
                transform={
                    activeIndex === 0
                        ? "translateX(5px)" // nudge right for first
                        : activeIndex === tabs.length - 1
                            ? "translateX(-5px)" // nudge left for last
                            : "translateX(0)"
                }
                _before={{
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                        'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 50%, rgba(255, 255, 255, 0.1) 100%)',
                    borderRadius: '20px',
                    pointerEvents: 'none',
                }}
                _after={{
                    content: '""',
                    position: 'absolute',
                    top: '2px',
                    left: '20%',
                    right: '20%',
                    height: '2px',
                    background:
                        'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
                    borderRadius: '1px',
                }}
            />

            {tabs.map((tab, index) => (
                <Box
                    key={tab.id}
                    width={`${100 / tabs.length}%`}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    position="relative"
                    zIndex={3}
                    transform={activeSection === tab.id ? 'scale(1.05)' : 'scale(1)'}
                    transition="transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)"
                >
                    <NavItem
                        icon={tab.icon}
                        label={tab.label}
                        isActive={activeSection === tab.id}
                        onClick={() => handleClick(tab)}
                    />
                </Box>
            ))}

            {/* Ambient glow effect */}
            <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                width="120%"
                height="120%"
                background="radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%)"
                borderRadius="50%"
                pointerEvents="none"
                zIndex={1}
                animation="pulse 4s ease-in-out infinite"
                sx={{
                    '@keyframes pulse': {
                        '0%, 100%': { opacity: 0.3, transform: 'translate(-50%, -50%) scale(1)' },
                        '50%': { opacity: 0.6, transform: 'translate(-50%, -50%) scale(1.05)' }
                    }
                }}
            />

            {/* Top edge highlight */}
            <Box
                position="absolute"
                top="0"
                left="10%"
                right="10%"
                height="1px"
                background="linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 30%, rgba(255, 255, 255, 0.6) 50%, rgba(255, 255, 255, 0.4) 70%, transparent 100%)"
                borderTopRadius="24px"
                pointerEvents="none"
                zIndex={2}
            />

            {/* Bottom edge subtle shadow */}
            <Box
                position="absolute"
                bottom="0"
                left="5%"
                right="5%"
                height="1px"
                background="linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.1) 50%, transparent 100%)"
                borderBottomRadius="24px"
                pointerEvents="none"
                zIndex={1}
            />
        </Flex>
    );
};

export default BottomNav;